import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING = {
  role: "assistant",
  text: "Welcome to SK Media Events. I'm Aurora, your event concierge — ask me about drum circles, team building, venues, artists or bands. Or simply tell me the event you're dreaming of, and I'll shape it with you.",
};

function getSessionId() {
  let sid = localStorage.getItem("sk_chat_session");
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem("sk_chat_session", sid);
  }
  return sid;
}

const renderText = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? (
      <strong key={i} className="text-[#f3e5ab] font-semibold">
        {part.slice(2, -2)}
      </strong>
    ) : (
      part
    )
  );

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const sid = getSessionId();
    fetch(`${API}/chat/${sid}`)
      .then((r) => r.json())
      .then((history) => {
        if (Array.isArray(history) && history.length > 0) setMessages(history);
      })
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      if (!res.ok || !res.body) throw new Error("chat failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const { t } = JSON.parse(payload);
            if (t) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  text: copy[copy.length - 1].text + t,
                };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "I'm having trouble connecting right now. Please call us at +91 97411 06724 or use the enquiry form below.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-open-button"
        onClick={() => setOpen(true)}
        aria-label="Open AI concierge"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2.4, type: "spring", stiffness: 200, damping: 14 }}
        className={`fixed bottom-6 left-6 z-[70] group ${open ? "hidden" : ""}`}
      >
        <span className="relative flex items-center gap-2 pl-4 pr-5 h-14 rounded-full border border-[rgba(212,175,55,0.5)] bg-[#121215]/95 backdrop-blur text-[#f3e5ab] shadow-[0_8px_30px_rgba(212,175,55,0.25)] group-hover:bg-[#d4af37] group-hover:text-[#09090b] transition-colors duration-300">
          <Sparkles size={20} />
          <span className="text-xs uppercase tracking-[0.2em] font-semibold">
            Ask Aurora
          </span>
        </span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-6 left-6 z-[75] w-[calc(100vw-3rem)] sm:w-[390px] h-[560px] max-h-[75vh] flex flex-col bg-[#0d0d10] border border-[rgba(212,175,55,0.35)] shadow-[0_20px_60px_rgba(0,0,0,0.7)]"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-[rgba(212,175,55,0.25)] bg-[#121215]">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center bg-gradient-to-br from-[#f3e5ab] via-[#d4af37] to-[#aa771c] text-[#09090b]">
                  <Sparkles size={17} />
                </span>
                <div>
                  <p className="font-cinzel text-sm tracking-[0.2em] text-[#f3e5ab]">
                    AURORA
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                    AI Event Concierge
                  </p>
                </div>
              </div>
              <button
                data-testid="chat-close-button"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                className="text-zinc-500 hover:text-[#d4af37] transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div
              ref={scrollRef}
              data-testid="chat-messages"
              className="flex-1 overflow-y-auto px-4 py-5 flex flex-col gap-4"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-message-${m.role}-${i}`}
                  className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "self-end bg-[#d4af37] text-[#09090b]"
                      : "self-start bg-[#121215] border border-[rgba(212,175,55,0.2)] text-zinc-300"
                  }`}
                >
                  {m.text
                    ? renderText(m.text)
                    : busy && i === messages.length - 1 ? (
                      <span className="text-[#d4af37] animate-gold-pulse">
                        Composing…
                      </span>
                    ) : (
                      ""
                    )}
                </div>
              ))}
            </div>

            <div className="px-4 py-4 border-t border-[rgba(212,175,55,0.25)] bg-[#121215] flex items-center gap-3">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Describe your event…"
                maxLength={2000}
                className="flex-1 bg-[#09090b] border border-[rgba(212,175,55,0.25)] focus:border-[#d4af37] outline-none px-4 py-3 text-sm text-white placeholder:text-zinc-600 transition-colors"
              />
              <button
                data-testid="chat-send-button"
                onClick={send}
                disabled={busy || !input.trim()}
                aria-label="Send message"
                className="w-11 h-11 shrink-0 flex items-center justify-center bg-[#d4af37] text-[#09090b] hover:bg-[#f3e5ab] transition-colors disabled:opacity-50"
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
